import { useTheme } from '../../context/ThemeContext.jsx';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export default function Toolbar({
  searchQuery, setSearchQuery,
  filterCategory, setFilterCategory,
  filterLicense, setFilterLicense,
  sortBy, sortOrder, onSortChange,
  showActivatedOnly, setShowActivatedOnly,
  compareMode, setCompareMode, comparisonFontIds, setComparisonFontIds, onLaunchComparison,
  viewMode, setViewMode,
  activeLetter, setActiveLetter, letterCounts,
  setPage,
  onShowSmartSearch, onShowPlayground, onShowDuplicates, onShowHealth,
}) {
  const { theme } = useTheme();

  const toggleStyle = (isActive) => ({
    background: isActive ? theme.accentBg : 'transparent',
    border: `1px solid ${isActive ? theme.accentBorder : theme.border}`,
    borderRadius: 0,
    color: isActive ? theme.accent : theme.text,
    fontSize: 12, padding: '7px 12px', cursor: 'pointer',
    fontFamily: 'inherit', textTransform: 'lowercase',
  });

  return (
    <>
      {/* Toolbar */}
      <div style={{
        padding: '12px 24px',
        borderBottom: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px 12px',
      }}>
        <div style={{
          flex: 1, minWidth: 220, display: 'flex',
        }}>
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center',
            border: `1px solid ${theme.borderMedium}`,
          }}>
            <span style={{ color: theme.textFaint, fontSize: 14, margin: '0 0 0 14px' }}>⌕</span>
            <input
              type="text"
              placeholder="search fonts, designers, categories"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                background: 'none', border: 'none', color: theme.text,
                fontSize: 13, height: 36, padding: '0 6px', width: '100%', outline: 'none',
                fontFamily: 'inherit',
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  background: 'none', border: 'none', color: theme.textFaint,
                  cursor: 'pointer', fontSize: 14, padding: '0 10px',
                }}
              >✕</button>
            )}
          </div>
          <button
            onClick={onShowSmartSearch}
            title="ai smart search"
            style={{
              height: 36, width: 44, marginLeft: -1,
              background: theme.fill, border: 0, cursor: 'pointer',
              color: theme.fillFg, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
            }}
          >ai</button>
        </div>

        <select
          value={filterCategory}
          onChange={e => { setFilterCategory(e.target.value); setPage(1); }}
          style={{
            height: 36, padding: '0 10px',
            background: 'transparent', border: `1px solid ${theme.border}`,
            borderRadius: 0, color: theme.text,
            fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <option value="All">all types</option>
          <option value="sans-serif">sans-serif</option>
          <option value="serif">serif</option>
          <option value="monospace">monospace</option>
          <option value="display">display</option>
          <option value="handwriting">handwriting</option>
        </select>

        <select
          value={filterLicense}
          onChange={e => { setFilterLicense(e.target.value); setPage(1); }}
          style={{
            height: 36, padding: '0 10px',
            background: 'transparent', border: `1px solid ${theme.border}`,
            borderRadius: 0, color: theme.text,
            fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <option value="All">all licenses</option>
          <option value="unknown">unknown</option>
          <option value="commercial">commercial</option>
          <option value="open-source">open source</option>
          <option value="personal">personal</option>
          <option value="trial">trial</option>
        </select>

        <select
          value={`${sortBy}:${sortOrder}`}
          onChange={e => onSortChange(e.target.value)}
          style={{
            height: 36, padding: '0 10px',
            background: 'transparent', border: `1px solid ${theme.border}`,
            borderRadius: 0, color: theme.text,
            fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <option value="name:ASC">name a&ndash;z</option>
          <option value="name:DESC">name z&ndash;a</option>
          <option value="created_at:DESC">newest first</option>
          <option value="created_at:ASC">oldest first</option>
          <option value="style_count:DESC">most styles</option>
          <option value="style_count:ASC">fewest styles</option>
          <option value="total_size:DESC">largest size</option>
          <option value="total_size:ASC">smallest size</option>
          <option value="rating:DESC">highest rated</option>
          <option value="rating:ASC">lowest rated</option>
        </select>

        <button
          onClick={() => { setShowActivatedOnly(!showActivatedOnly); setPage(1); }}
          style={{ ...toggleStyle(showActivatedOnly), height: 36 }}
        >active only</button>

        <button
          onClick={() => { setCompareMode(!compareMode); if (compareMode) setComparisonFontIds([]); }}
          style={{ ...toggleStyle(compareMode), height: 36 }}
        >compare</button>

        <button
          onClick={onShowPlayground}
          title="CSS Playground"
          style={{ ...toggleStyle(false), height: 36 }}
        >css</button>

        <button
          onClick={onShowDuplicates}
          title="Find duplicate fonts"
          style={{ ...toggleStyle(false), height: 36 }}
        >duplicates</button>

        <button
          onClick={onShowHealth}
          title="Font health dashboard"
          style={{ ...toggleStyle(false), height: 36 }}
        >health</button>

        <div style={{ display: 'flex' }}>
          <button
            onClick={() => setViewMode('grid')}
            style={{
              height: 36, width: 36,
              background: viewMode === 'grid' ? theme.fill : 'transparent',
              color: viewMode === 'grid' ? theme.fillFg : theme.text,
              border: `1px solid ${theme.border}`, cursor: 'pointer', fontSize: 12, borderRadius: 0,
            }}
          >⊞</button>
          <button
            onClick={() => setViewMode('list')}
            style={{
              height: 36, width: 36, marginLeft: -1,
              background: viewMode === 'list' ? theme.fill : 'transparent',
              color: viewMode === 'list' ? theme.fillFg : theme.text,
              border: `1px solid ${theme.border}`, cursor: 'pointer', fontSize: 12, borderRadius: 0,
            }}
          >☰</button>
        </div>
      </div>

      {/* Compare bar */}
      {compareMode && (
        <div style={{
          padding: '8px 24px', borderBottom: `1px solid ${theme.borderLight}`,
          display: 'flex', alignItems: 'center', gap: 12,
          background: theme.accentBg,
        }}>
          <span style={{ fontSize: 12, color: theme.text, textTransform: 'lowercase' }}>
            compare: {comparisonFontIds.length}/3 selected
          </span>
          {comparisonFontIds.length >= 2 && (
            <button
              onClick={onLaunchComparison}
              style={{
                padding: '4px 12px', fontSize: 11,
                background: theme.fill, border: 'none', borderRadius: 0, color: theme.fillFg,
                cursor: 'pointer', fontFamily: 'inherit', textTransform: 'lowercase',
              }}
            >compare now</button>
          )}
          <button
            onClick={() => { setCompareMode(false); setComparisonFontIds([]); }}
            style={{
              background: 'none', border: 'none', color: theme.accent,
              cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', textTransform: 'lowercase',
            }}
          >cancel</button>
        </div>
      )}

      {/* Alphabet Bar */}
      <div style={{
        padding: '6px 24px', borderBottom: `1px solid ${theme.borderLight}`,
        display: 'flex', alignItems: 'center', gap: 2,
        flexWrap: 'nowrap', overflowX: 'auto',
      }}>
        {ALPHABET.map(letter => {
          const count = letterCounts[letter] || 0;
          const isActive = activeLetter === letter;
          return (
            <button
              key={letter}
              onClick={() => { setActiveLetter(isActive ? null : letter); setPage(1); }}
              style={{
                width: 26, height: 26,
                background: 'none',
                border: 'none', borderBottom: `2px solid ${isActive ? theme.accent : 'transparent'}`,
                color: isActive ? theme.text : theme.textTertiary,
                fontSize: 11, fontWeight: 400, cursor: count > 0 || isActive ? 'pointer' : 'default',
                opacity: count > 0 || isActive ? 1 : 0.35,
                fontFamily: 'inherit', padding: 0,
              }}
              title={`${letter}: ${count} fonts`}
            >{letter}</button>
          );
        })}
        <button
          onClick={() => { setActiveLetter(activeLetter === '#' ? null : '#'); setPage(1); }}
          style={{
            width: 26, height: 26,
            background: 'none',
            border: 'none', borderBottom: `2px solid ${activeLetter === '#' ? theme.accent : 'transparent'}`,
            color: activeLetter === '#' ? theme.text : theme.textTertiary,
            fontSize: 11, fontWeight: 400, cursor: 'pointer',
            fontFamily: 'inherit', padding: 0,
          }}
          title="Non-alphabetic"
        >#</button>
        {activeLetter && (
          <button
            onClick={() => { setActiveLetter(null); setPage(1); }}
            style={{
              marginLeft: 8, background: 'none', border: 'none',
              color: theme.accent, cursor: 'pointer',
              fontSize: 11, fontFamily: 'inherit', textTransform: 'lowercase',
            }}
          >clear</button>
        )}
      </div>
    </>
  );
}
